import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  FormControlLabel,
  FormHelperText,
  Grid,
  Input,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import EditText from "components/editText";
import MainContainer from "layout/MainContainer";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAddInvestmentPlanMutation,
  useLazyGetInvestmentPlanByIdQuery,
  useUpdateInvestmentPlanMutation,
} from "service/investment";
import { useFormik } from "formik";
import * as Yup from "yup";
import { generateEncryptedKeyBody } from "utils/crypto";
import { CommonBody } from "types/General";
import { showError, showToast } from "helpers";
import { isQuillEmpty } from "utils/commonFunctions";
import { isNumber } from "utils/validations";
import { CalendarMonth } from "@mui/icons-material";
import { UploadMedia } from "utils/mediaUpload";

const INVESTMENT_PAYOUT_CYCLE = {
  MONTHLY: 1,
  QUARTERLY: 2,
  HALF_YEARLY: 3,
  YEARLY: 4,
};

interface FormValues {
  name: string;
  description: string;
  minimumInvestment: string;
  expectedReturn: string;
  payoutCycle: string;
  tenureUnit: string;
  tenureValue: string;
  termsAndConditions: string;
}

const AddInvestmentPlan = () => {
  const navigate = useNavigate();

  const [addInvestmentPlan] = useAddInvestmentPlanMutation();
  const [getInvestmentPlanById] = useLazyGetInvestmentPlanByIdQuery();
  const [updateInvestmentPlan] = useUpdateInvestmentPlanMutation();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const { id } = useParams();
  const [terms, setTerms] = useState("");
  const [benefits, setBenefits] = useState("");

  const planOptions = [
    { id: INVESTMENT_PAYOUT_CYCLE.MONTHLY, label: "Monthly", icon: "📅" },
    { id: INVESTMENT_PAYOUT_CYCLE.QUARTERLY, label: "Quarterly", icon: "📆" },
    {
      id: INVESTMENT_PAYOUT_CYCLE.HALF_YEARLY,
      label: "Half-Yearly",
      icon: "🗓️",
    },
    { id: INVESTMENT_PAYOUT_CYCLE.YEARLY, label: "Yearly", icon: "📘" },
  ];
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [planData, setPlanData] = useState<{ unit: number; value: number }[]>(
    []
  );

  // Select individual
  const handlePlanChange = (id: number) => {
    setSelectedPlans((prev: any) =>
      prev.includes(id) ? prev.filter((p: any) => p !== id) : [...prev, id]
    );

    setPlanData((prev: any) => {
      if (prev.some((p: any) => p.unit === id)) {
        return prev.filter((p: any) => p.unit !== id);
      }
      return [...prev, { unit: id, value: 0 }];
    });
  };

  // Select all
  const handleAllPlanChange = (e: any) => {
    if (e.target.checked) {
      setSelectedPlans(planOptions.map((p: any) => p.id));
      setPlanData(planOptions.map((p) => ({ unit: p.id, value: 0 })));
    } else {
      setSelectedPlans([]);
      setPlanData([]);
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLoading(true);
    const file = event.target.files?.[0];
    if (!file) return setLoading(false);
    try {
      const res = await UploadMedia(file);
      const imageUrl = res?.data?.image;
      if (imageUrl) {
        setImageUrl(imageUrl);
        showToast("Image uploaded successfully.");
      }
    } catch {
      showError("Error uploading image.");
    }
    setLoading(false);
  };

  // ROI change
  const handlePlanDataChange = (unit: number, newValue: string) => {
    setPlanData((prev) =>
      prev.map((p) => (p.unit === unit ? { ...p, value: Number(newValue) } : p))
    );
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      description: "",
      minimumInvestment: "",
      // expectedReturn: "",
      // payoutCycle: "",
      // tenureUnit: "",
      // tenureValue: "",
    } as FormValues,
    validationSchema: Yup.object({
      name: Yup.string().required("Plan name is required"),
      description: Yup.string().required("Description is required"),
      minimumInvestment: Yup.string().required(
        "Minimum investment is required"
      ),
    }),
    onSubmit: async (values: FormValues) => {
      setLoading(true);

      if (planData.length === 0) {
        showError("Please select a ROI plan");
        return;
      }

      if (isQuillEmpty(values.description)) {
        showError("Please enter terms and conditions");
        return;
      }

      if (isQuillEmpty(benefits)) {
        showError("Please enter benefits");
        return;
      }

      try {
        const body = {
          image: imageUrl,
          name: values.name,
          description: values.description,
          minimumInvestment: values.minimumInvestment,
          // minimumTenure: {
          //   value: values.tenureValue,
          //   unit: values.tenureUnit,
          // },
          // expectedReturn: values.expectedReturn,
          // payoutCycle: values.payoutCycle,
          // tenure: {
          //   value: values.tenureValue,
          //   unit: values.tenureUnit,
          // },
          benefits: [benefits],
          termsAndConditions: terms,
          roiDetails: planData,
        };

        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        let response;

        if (id) {
          response = await updateInvestmentPlan({
            body: encryptedBody,
            id,
          }).unwrap();
        } else {
          response = await addInvestmentPlan({
            body: encryptedBody,
          }).unwrap();
        }

        setLoading(false);
        if (response?.statusCode === 200) {
          showToast(response?.message || "");
          navigate("/manage-investments/investment-plans");
        }
      } catch (error) {
        setLoading(false);
        showError((error as any)?.data?.message || "Failed to process request");
      }
    },
  });

  const getInvestmentPlanByIdApi = async () => {
    setLoading(true);
    try {
      const response = await getInvestmentPlanById({
        id,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        formik.setFieldValue("name", response?.data?.name);
        formik.setFieldValue("description", response?.data?.description);
        formik.setFieldValue(
          "minimumInvestment",
          response?.data?.minimumInvestment
        );
        formik.setFieldValue("expectedReturn", response?.data?.expectedReturn);
        formik.setFieldValue("payoutCycle", response?.data?.payoutCycle);
        formik.setFieldValue("tenureUnit", response?.data?.tenure?.unit);
        formik.setFieldValue("tenureValue", response?.data?.tenure?.value);
        setBenefits(response?.data?.benefits[0]?.title);
        setTerms(response?.data?.termsAndConditions || "");
        setPlanData(response?.data?.roiDetails || []);
        setSelectedPlans(response?.data?.roiDetails?.map((p: any) => p.unit));
        setImageUrl(response?.data?.image || "");
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    if (id) {
      getInvestmentPlanByIdApi();
    }
  }, [id]);

  return (
    <MainContainer>
      {/* <Loader isLoad={loading} /> */}
      <div className="main_title fdx">
        <div>
          <h1>{id ? "Edit" : "Add"} Investment Plan</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate("/listings/myList")}>
              My Listings
            </span>{" "}
            - {id ? "Edit" : "Add"} Investment Plan
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_33">
              <label>Plan Image</label>
              <div
                style={{
                  position: "relative",
                  width: 400,
                  height: 200,
                  minWidth: 120,
                  minHeight: 120,
                  border: "1.5px solid #ccc",
                  borderRadius: 8,
                  background: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={imageUrl || "/static/images/picture.png"}
                  alt="vehicle doc"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: imageUrl ? "cover" : "contain",
                    borderRadius: 8,
                    background: "#f5f5f5",
                  }}
                />
                <label
                  htmlFor="vehicle-doc-upload"
                  style={{
                    position: "absolute",
                    bottom: 8,
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#006039",
                    borderRadius: "50%",
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    border: "2px solid #fff",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    zIndex: 2,
                  }}
                >
                  <Input
                    sx={{ display: "none" }}
                    id="vehicle-doc-upload"
                    type="file"
                    inputProps={{ accept: "image/png,image/jpeg" }}
                    onChange={handleImageUpload}
                  />
                  <img
                    src="/static/images/edit2_icon.svg"
                    alt="edit"
                    style={{ width: 24, height: 24 }}
                  />
                </label>
              </div>
            </div>

            <div className="control_group w_50">
              <label>Plan Name</label>
              <TextField
                variant="outlined"
                placeholder="Plan Name"
                fullWidth
                hiddenLabel
                className="text_field"
                name="name"
                id="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.name && formik.errors.name}
                error={formik.touched.name && Boolean(formik.errors.name)}
              />
            </div>

            <div className="control_group w_50">
              <label>Minimum Investment Amount</label>
              <TextField
                variant="outlined"
                placeholder="Minimum Investment Amount"
                fullWidth
                hiddenLabel
                className="text_field"
                name="minimumInvestment"
                id="minimumInvestment"
                inputProps={{ maxLength: 4 }}
                value={formik.values.minimumInvestment}
                onChange={(val) => {
                  if (isNumber(val.target.value)) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.minimumInvestment &&
                  formik.errors.minimumInvestment
                }
                error={
                  formik.touched.minimumInvestment &&
                  Boolean(formik.errors.minimumInvestment)
                }
              />
            </div>

            {/* <div className="control_group w_50">
              <label>ROI % (Fixed Monthly)</label>
              <TextField
                variant="outlined"
                placeholder="ROI % (Fixed Monthly)"
                fullWidth
                hiddenLabel
                className="text_field"
                name="expectedReturn"
                id="expectedReturn"
                inputProps={{ maxLength: 4 }}
                value={formik.values.expectedReturn}
                onChange={(val) => {
                  if (isNumber(val.target.value)) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.expectedReturn && formik.errors.expectedReturn
                }
                error={
                  formik.touched.expectedReturn &&
                  Boolean(formik.errors.expectedReturn)
                }
              />
            </div>

            <div className="control_group w_50">
              <label>Payout Cycle</label>
              <Select
                fullWidth
                labelId="payoutCycle"
                name="payoutCycle"
                displayEmpty
                value={formik.values.payoutCycle}
                onChange={(e) =>
                  formik.setFieldValue("payoutCycle", e.target.value)
                }
                onBlur={formik.handleBlur}
                error={
                  formik.touched.payoutCycle &&
                  Boolean(formik.errors.payoutCycle)
                }
              >
                <MenuItem disabled value="">
                  select payout cycle
                </MenuItem>

                <MenuItem value={1}>Monthly</MenuItem>
                <MenuItem value={2}>Quarterly</MenuItem>
                <MenuItem value={3}>Yearly</MenuItem>
              </Select>
              {formik.touched.payoutCycle && formik.errors.payoutCycle && (
                <FormHelperText error>
                  {formik.errors.payoutCycle}
                </FormHelperText>
              )}
            </div>

            <div className="control_group w_50">
              <label>Investment Tenure</label>
              <Select
                fullWidth
                labelId="condition"
                name="condition"
                value={formik.values.tenureUnit}
                onChange={(e) =>
                  formik.setFieldValue("tenureUnit", e.target.value)
                }
                displayEmpty
                onBlur={formik.handleBlur}
                error={
                  formik.touched.tenureUnit && Boolean(formik.errors.tenureUnit)
                }
              >
                <MenuItem disabled value="">
                  select investment tenure
                </MenuItem>

                <MenuItem value={1}>Months</MenuItem>
                <MenuItem value={2}>Years</MenuItem>
              </Select>
              {formik.touched.tenureUnit && formik.errors.tenureUnit && (
                <FormHelperText error>
                  {formik.errors.tenureUnit}
                </FormHelperText>
              )}
            </div>

            <div className="control_group w_50">
              <label>Investment Tenure Value</label>
              <TextField
                variant="outlined"
                placeholder="Investment Tenure Value"
                fullWidth
                hiddenLabel
                className="text_field"
                name="tenureValue"
                id="tenureValue"
                inputProps={{ maxLength: 4 }}
                value={formik.values.tenureValue}
                onChange={(val) => {
                  if (isNumber(val.target.value)) {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.tenureValue && formik.errors.tenureValue
                }
                error={
                  formik.touched.tenureValue &&
                  Boolean(formik.errors.tenureValue)
                }
              />
            </div> */}

            <div className="control_group w_50">
              <label>Plan Description</label>
              <TextField
                variant="outlined"
                placeholder="Annual Benefits (Text Description)"
                fullWidth
                hiddenLabel
                className="text_field"
                name="description"
                id="description"
                multiline
                minRows={4}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.description && formik.errors.description
                }
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
              />
            </div>

            {/* <div className="control_group w_50">
              <label>Annual Benefits (Text Description)</label>
              <TextField
                variant="outlined"
                placeholder="Annual Benefits (Text Description)"
                fullWidth
                hiddenLabel
                className="text_field"
                name="benefits"
                id="benefits"
                multiline
                minRows={4}
                value={formik.values.benefits}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.benefits && formik.errors.benefits}
                error={
                  formik.touched.benefits && Boolean(formik.errors.benefits)
                }
              />
            </div> */}

            <div className="control_group w_100">
              <label>Benefits</label>
              {benefits && id && (
                <EditText content={benefits ?? ""} setContent={setBenefits} />
              )}
              {!id && (
                <EditText content={benefits ?? ""} setContent={setBenefits} />
              )}
            </div>

            <div className="control_group w_100">
              <label>Terms & Conditions</label>
              {terms && id && (
                <EditText content={terms ?? ""} setContent={setTerms} />
              )}
              {!id && <EditText content={terms ?? ""} setContent={setTerms} />}
            </div>

            <div className="control_group w_100">
              <Card variant="outlined">
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarMonth />
                        ROI Management
                      </Box>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              selectedPlans.length === planOptions.length
                            }
                            onChange={handleAllPlanChange}
                          />
                        }
                        label="All"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  }
                  subheader="Select investment plan durations and set ROI (%) for each plan"
                />

                <CardContent>
                  {/* Select Plans */}
                  <Typography variant="subtitle1" gutterBottom>
                    Available ROI Plans
                  </Typography>

                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={4}
                    flexWrap="nowrap"
                    mb={2}
                  >
                    {planOptions.map((option: any) => (
                      <FormControlLabel
                        key={option.id}
                        control={
                          <Checkbox
                            checked={selectedPlans.includes(option.id)}
                            onChange={() => handlePlanChange(option.id)}
                          />
                        }
                        label={`${option.icon} ${option.label}`}
                      />
                    ))}
                  </Box>

                  {/* Selected Plans & ROI */}
                  {selectedPlans.length > 0 && (
                    <Box mt={3}>
                      <Typography variant="subtitle2">
                        Selected Plans
                      </Typography>

                      <Box display="flex" gap={1} flexWrap="wrap" my={1}>
                        {selectedPlans.map((id) => {
                          const item = planOptions.find(
                            (p: any) => p.id === id
                          );
                          return (
                            <Chip
                              key={id}
                              label={`${item?.icon} ${item?.label}`}
                              variant="outlined"
                            />
                          );
                        })}
                      </Box>

                      <Typography
                        variant="subtitle1"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        mt={2}
                        mb={1}
                      >
                        Set ROI (%)
                      </Typography>

                      <Grid container spacing={2}>
                        {selectedPlans.map((id: any) => {
                          const plan = planOptions.find(
                            (p: any) => p.id === id
                          );
                          const current = planData.find(
                            (p: any) => p.unit === id
                          );

                          return (
                            <Grid item xs={12} md={4} key={id}>
                              <TextField
                                placeholder={`${plan?.label} ROI %`}
                                type="text"
                                fullWidth
                                hiddenLabel
                                variant="outlined"
                                value={current?.value || ""}
                                onChange={(e: any) => {
                                  const cleaned = e.target.value.replace(
                                    /[^0-9.]/g,
                                    ""
                                  );
                                  handlePlanDataChange(id, cleaned);
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="form_end_btn">
            <Button
              className="btn btn_secondary"
              type="submit"
              // disabled={isLoading}
            >
              {/* {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Submit"
              )} */}
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddInvestmentPlan;
